import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@car/ui/components/alert-dialog";
import { Button } from "@car/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@car/ui/components/card";
import { Checkbox } from "@car/ui/components/checkbox";
import { FormAlert, useAppForm } from "@car/ui/components/form";
import { Skeleton } from "@car/ui/components/skeleton";
import { optionalText } from "@car/ui/lib/form-rules";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  CalendarAdd01Icon,
  CheckmarkCircle02Icon,
  CircleIcon,
  RepairIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  PageHeader,
  PanelEmpty,
  PanelQueryError,
  RepairStatusBadge,
} from "@/components/panel-bits";
import { faDate, faDateTimeShort, faPlainNumber, faToman } from "@/lib/format";
import {
  appointmentStatusLabels,
  isOpenAppointment,
  repairStepLabels,
  RESCHEDULE_CUTOFF_HOURS,
  serviceTypeLabels,
  warrantyDecisionLabels,
  type Appointment,
  type RepairEstimate,
  type RepairOrder,
} from "@/lib/panel";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/appointments/")({
  head: () => ({ meta: [{ title: "رزرو خدمات | پنل مشتریان تابان خودرو" }] }),
  component: AppointmentsPage,
});

const testDriveStatusLabels = {
  scheduled: "رزرو شده",
  done: "انجام شد",
  no_show: "مراجعه نکردید",
  cancelled: "لغو شد",
};
const testDriveStatusLabel = (status: string) =>
  Object.entries(testDriveStatusLabels).find(([key]) => key === status)?.[1] ?? status;

/** تست‌درایوهایی که با شماره‌ی این مشتری رزرو شده (از سایت یا فروش)؛ تغییر و لغو با شعبه است */
function TestDrivesSection() {
  const query = useQuery(orpc.panel.appointments.testDrives.queryOptions());
  const drives = query.data?.testDrives ?? [];
  if (drives.length === 0) return null;
  return (
    <section aria-label="تست‌درایوها">
      <h2 className="font-bold">تست‌درایوهای شما</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        برای تغییر یا لغو تست‌درایو با شعبه تماس بگیرید؛ شماره‌ی شعبه‌ها در{" "}
        <a href="/contact" className="font-bold underline underline-offset-4">
          صفحه‌ی تماس
        </a>{" "}
        است.
      </p>
      <div className="grid gap-2">
        {drives.map((drive) => (
          <div
            key={drive.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-3 text-sm"
          >
            <span className="font-bold">
              {drive.vehicleName}
              {SEP}
              {drive.branchName}
            </span>
            <span className="text-muted-foreground">
              {faDateTimeShort(drive.scheduledIso)}
              {SEP}
              {testDriveStatusLabel(drive.status)}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function AppointmentsPage() {
  const query = useQuery(orpc.panel.appointments.list.queryOptions());

  if (query.isPending) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  if (query.isError) {
    return <PanelQueryError title="نوبت‌ها و سوابق رزرو بارگیری نشد" query={query} />;
  }

  const data = query.data;
  const active = data.appointments.filter((item) => isOpenAppointment(item.status, item.dateIso));
  const inactive = data.appointments.filter(
    (item) => !isOpenAppointment(item.status, item.dateIso),
  );
  const openRepairs = data.repairOrders.filter((order) => order.status !== "delivered");

  return (
    <div className="grid gap-6">
      <PageHeader
        title="رزرو خدمات"
        description="نوبت‌های فعال، وضعیت خودرو در تعمیرگاه و سابقه رزروها."
        action={
          <Button nativeButton={false} render={<Link to="/panel/appointments/new" />}>
            <HugeiconsIcon icon={CalendarAdd01Icon} strokeWidth={1.8} />
            رزرو نوبت جدید
          </Button>
        }
      />

      {openRepairs.length > 0 ? (
        <section className="grid gap-4" aria-label="خودرو در تعمیرگاه">
          {openRepairs.map((order) => (
            <Card key={order.id}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <HugeiconsIcon icon={RepairIcon} strokeWidth={1.8} className="size-5" />
                    {order.title}
                    {SEP}
                    {order.vehicleName}
                  </CardTitle>
                  <RepairStatusBadge status={order.status} />
                </div>
              </CardHeader>
              <CardContent className="grid gap-4">
                <p className="text-sm text-muted-foreground">
                  شماره پذیرش {faPlainNumber(order.number)}
                  {SEP}
                  {order.branchName}
                </p>
                {order.note ? <p className="text-sm text-muted-foreground">{order.note}</p> : null}
                <RepairDetails order={order} />
                {order.estimates.map((estimate) => (
                  <EstimateCard key={estimate.id} orderId={order.id} estimate={estimate} />
                ))}
                <ol className="grid gap-0">
                  {order.timeline.map((step, index) => {
                    const isLast = index === order.timeline.length - 1;
                    return (
                      <li key={index} className="relative flex gap-3 pb-4 last:pb-0">
                        <span className="flex flex-col items-center">
                          <span
                            className={cn(
                              "grid size-6 shrink-0 place-items-center rounded-full",
                              isLast
                                ? "bg-primary text-primary-foreground"
                                : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
                            )}
                          >
                            <HugeiconsIcon
                              icon={CheckmarkCircle02Icon}
                              strokeWidth={2}
                              className="size-4"
                            />
                          </span>
                          {!isLast ? <span className="w-px flex-1 bg-border" /> : null}
                        </span>
                        <div className="grid gap-0.5 pb-1">
                          <span className="text-sm font-bold">{repairStepLabels[step.status]}</span>
                          <span className="text-xs text-muted-foreground">
                            {faDateTimeShort(step.dateIso)}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <p className="text-sm text-muted-foreground">
                  {order.branchName}
                  {order.estimatedReadyIso
                    ? `${SEP}تحویل تقریبی ${faDate(order.estimatedReadyIso)}`
                    : ""}
                </p>
              </CardContent>
            </Card>
          ))}
        </section>
      ) : null}

      <section aria-label="نوبت‌های فعال">
        <h2 className="font-bold">نوبت‌های فعال</h2>
        <p className="mb-3 text-sm text-muted-foreground">
          زمان هر نوبت را تا {faPlainNumber(RESCHEDULE_CUTOFF_HOURS)} ساعت پیش از آن از همین‌جا تغییر
          دهید؛ نوبت فعلی تا گرفتن زمان تازه برای شما می‌ماند. یک روز پیش از نوبت پیامک یادآوری
          می‌فرستیم.
        </p>
        {active.length === 0 ? (
          <PanelEmpty
            icon={CalendarAdd01Icon}
            title="نوبت فعالی ندارید"
            description="برای سرویس دوره‌ای یا تعمیرات، همین حالا نوبت بگیرید."
            action={
              <Button
                variant="outline"
                nativeButton={false}
                render={<Link to="/panel/appointments/new" />}
              >
                رزرو نوبت
              </Button>
            }
          />
        ) : (
          <div className="grid gap-3">
            {active.map((appointment) => (
              <Card key={appointment.id}>
                <CardContent className="flex flex-wrap items-center justify-between gap-3">
                  <div className="grid gap-1">
                    <span className="font-bold">
                      {serviceTypeLabels[appointment.serviceType]}
                      {SEP}
                      {appointment.vehicleName}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {faDate(appointment.dateIso)}، ساعت {appointment.slot}
                      {SEP}
                      {appointment.branchName}
                      {appointment.status === "arrived" ? `${SEP}مراجعه کرده‌اید` : ""}
                    </span>
                    {appointment.note ? (
                      <span className="text-sm text-muted-foreground">
                        توضیح شما: {appointment.note}
                      </span>
                    ) : null}
                  </div>
                  {appointment.status === "scheduled" ? (
                    <div className="flex flex-wrap items-center gap-2">
                      {/* همان نوبت جابه‌جا می‌شود؛ جای قبلی فقط وقتی آزاد می‌شود که جای تازه گرفته شود */}
                      <Button
                        variant="outline"
                        size="sm"
                        nativeButton={false}
                        render={
                          <Link
                            to="/panel/appointments/new"
                            search={{ reschedule: appointment.id }}
                          />
                        }
                      >
                        تغییر زمان
                      </Button>
                      <CancelAppointmentDialog appointment={appointment} />
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <TestDrivesSection />

      {inactive.length > 0 ? (
        <section aria-label="سابقه رزروها">
          <h2 className="mb-3 font-bold">سابقه رزروها</h2>
          <div className="grid gap-2">
            {inactive.map((appointment) => (
              <div
                key={appointment.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-3 text-sm"
              >
                <span>
                  {serviceTypeLabels[appointment.serviceType]}
                  {SEP}
                  {appointment.vehicleName}
                </span>
                <span className="text-muted-foreground">
                  {faDate(appointment.dateIso)}
                  {SEP}
                  {appointment.status === "cancelled" && appointment.cancelledByBranch
                    ? "لغو شده از سوی شعبه"
                    : appointmentStatusLabels[appointment.status]}
                </span>
                {appointment.status === "cancelled" && appointment.cancelReason ? (
                  <span className="basis-full text-xs text-muted-foreground">
                    دلیل لغو: {appointment.cancelReason}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

const cancelSchema = z.object({ reason: optionalText("دلیل لغو", 300) });

/** لغو با دلیل اختیاری که پذیرش شعبه می‌بیند */
function CancelAppointmentDialog({ appointment }: { appointment: Appointment }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const mutation = useMutation(orpc.panel.appointments.cancel.mutationOptions());
  const form = useAppForm({
    defaultValues: { reason: "" },
    validators: { onBlur: cancelSchema },
    onSubmit: async ({ value }) => {
      setError("");
      const result = await mutation
        .mutateAsync({ id: appointment.id, reason: value.reason.trim() })
        .catch(() => null);
      if (!result) return setError("لغو انجام نشد؛ اتصال را بررسی و دوباره تلاش کنید.");
      if (!result.ok) return setError(result.reason);
      toast.success("نوبت با موفقیت لغو شد");
      setOpen(false);
      void queryClient.invalidateQueries();
    },
  });
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          form.reset();
          setError("");
        }
        setOpen(next);
      }}
    >
      <AlertDialogTrigger render={<Button variant="destructive" size="sm" />}>
        لغو نوبت
      </AlertDialogTrigger>
      <AlertDialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <AlertDialogHeader>
              <AlertDialogTitle>این نوبت لغو شود؟</AlertDialogTitle>
              <AlertDialogDescription>
                نوبت {serviceTypeLabels[appointment.serviceType]} در تاریخ{" "}
                {faDate(appointment.dateIso)} لغو می‌شود. اگر فقط زمان مناسب نیست، به‌جای لغو «تغییر
                زمان» را بزنید.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <form.AppField name="reason">
              {(field) => (
                <field.TextareaField
                  label="دلیل لغو (اختیاری)"
                  placeholder="مثلاً سفر پیش آمد؛ به ما کمک می‌کند بهتر برنامه‌ریزی کنیم."
                  maxLength={300}
                  rows={2}
                />
              )}
            </form.AppField>
            <FormAlert message={error} />
            <AlertDialogFooter>
              <AlertDialogCancel>انصراف</AlertDialogCancel>
              <form.SubmitButton variant="destructive" pending={mutation.isPending}>
                بله، لغو شود
              </form.SubmitButton>
            </AlertDialogFooter>
          </form.Form>
        </form.AppForm>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** تصمیم گارانتی، کارهای انجام‌شده و صورت‌حساب جاری، تا مالک پیش از تحویل بداند چه خبر است */
function RepairDetails({ order }: { order: RepairOrder }) {
  const { warranty } = order;
  return (
    <div className="grid gap-3">
      <p
        className={cn(
          "rounded-lg border px-3 py-2 text-sm",
          warranty.decision === "covered" &&
            "border-emerald-500/40 bg-emerald-500/5 text-emerald-800 dark:text-emerald-300",
        )}
      >
        <span className="font-bold">گارانتی: {warrantyDecisionLabels[warranty.decision]}</span>
        {warranty.validText ? `${SEP}${warranty.validText}` : ""}
        {warranty.note ? (
          <span className="block text-muted-foreground">{warranty.note}</span>
        ) : null}
      </p>
      {order.operations.length > 0 ? (
        <div className="grid gap-1.5">
          <h3 className="text-sm font-bold">کارهای این تعمیر</h3>
          <ul className="grid gap-1 text-sm">
            {order.operations.map((operation) => (
              <li key={operation.id} className="flex items-center gap-2">
                <HugeiconsIcon
                  icon={operation.done ? CheckmarkCircle02Icon : CircleIcon}
                  strokeWidth={1.8}
                  className={cn(
                    "size-4 shrink-0",
                    operation.done
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-muted-foreground",
                  )}
                />
                <span>{operation.title}</span>
                <span className="text-xs text-muted-foreground">
                  {operation.done ? "انجام شد" : "در نوبت"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {order.lines.length > 0 ? (
        <div className="grid gap-1.5">
          <h3 className="text-sm font-bold">صورت‌حساب تا این لحظه</h3>
          <ul className="grid gap-1 text-sm">
            {order.lines.map((line, index) => (
              <li key={index} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <span className={line.amount < 0 ? "text-emerald-700 dark:text-emerald-400" : ""}>
                    {line.title}
                  </span>
                  {/* ردیف تخفیف (منفی) نه اجرت است نه قطعه */}
                  {line.amount < 0 ? null : (
                    <span className="text-xs text-muted-foreground">
                      {line.kind === "service" ? "اجرت" : "قطعه"}
                    </span>
                  )}
                </span>
                <span className="font-bold">{faToman(line.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-2 border-t pt-1.5 text-sm">
            <span className="font-bold">جمع فعلی</span>
            <span className="font-black">{faToman(order.runningTotal)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            فاکتور نهایی هنگام تحویل خودرو صادر می‌شود و با کارهایی که بعداً تأیید کنید تغییر می‌کند.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function EstimateCard({ orderId, estimate }: { orderId: string; estimate: RepairEstimate }) {
  const queryClient = useQueryClient();
  // هر ردیف جدا تأیید می‌شود؛ پیش‌فرض همه
  const [chosen, setChosen] = useState<ReadonlySet<number>>(
    () => new Set(estimate.lines.map((line) => line.index)),
  );
  const total = estimate.lines.reduce((sum, line) => sum + line.amount, 0);
  const chosenTotal = estimate.lines
    .filter((line) => chosen.has(line.index))
    .reduce((sum, line) => sum + line.amount, 0);
  const partial = chosen.size < estimate.lines.length;

  const mutation = useMutation(
    orpc.panel.appointments.decideEstimate.mutationOptions({
      onSuccess: (result, variables) => {
        if (result.ok) {
          toast.success(
            !variables.approve
              ? "پیش‌فاکتور رد شد؛ کار طبق دستور اولیه ادامه می‌یابد"
              : variables.lineIndexes
                ? "موارد انتخاب‌شده تأیید شد و در برنامه کار قرار گرفت"
                : "پیش‌فاکتور تأیید شد و در برنامه کار قرار گرفت",
          );
          queryClient.invalidateQueries();
        } else {
          toast.error(result.reason);
        }
      },
      onError: () => toast.error("تصمیم ثبت نشد؛ اتصال را بررسی و دوباره تلاش کنید."),
    }),
  );

  if (estimate.status !== "pending") {
    const declined = estimate.lines.filter((line) => line.declined);
    const approvedTotal = estimate.lines
      .filter((line) => !line.declined)
      .reduce((sum, line) => sum + line.amount, 0);
    return (
      <div
        className={cn(
          "grid gap-1 rounded-lg p-3 text-sm",
          estimate.status === "approved"
            ? "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"
            : "bg-muted text-muted-foreground",
        )}
      >
        <p>
          پیش‌فاکتور «{estimate.title}» در تاریخ{" "}
          {estimate.decidedIso ? faDate(estimate.decidedIso) : ""}{" "}
          {estimate.status === "rejected"
            ? `به مبلغ ${faToman(total)} رد شد.`
            : declined.length > 0
              ? `به مبلغ ${faToman(approvedTotal)} بخشی تأیید شد.`
              : `به مبلغ ${faToman(total)} تأیید شد.`}
        </p>
        {estimate.status === "approved" && declined.length > 0 ? (
          <p className="text-muted-foreground">
            انجام نمی‌شود: {declined.map((line) => line.title).join("، ")}
          </p>
        ) : null}
      </div>
    );
  }

  const approve = () =>
    mutation.mutate({
      repairOrderId: orderId,
      estimateId: estimate.id,
      approve: true,
      // omitted = every line
      lineIndexes: partial ? [...chosen] : undefined,
    });

  return (
    <div className="grid gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-bold">پیش‌فاکتور: {estimate.title}</span>
        <span className="font-black">{faToman(total)}</span>
      </div>
      {estimate.note ? <p className="text-sm text-muted-foreground">{estimate.note}</p> : null}
      <fieldset className="grid gap-1 text-sm">
        <legend className="mb-1 text-xs text-muted-foreground">
          تیک هر موردی را که نمی‌خواهید انجام شود بردارید.
        </legend>
        {estimate.lines.map((line) => {
          const id = `est-${estimate.id}-${line.index}`;
          const on = chosen.has(line.index);
          return (
            <label
              key={line.index}
              htmlFor={id}
              className="flex min-h-11 cursor-pointer items-center justify-between gap-2 rounded-md px-1"
            >
              <span className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={on}
                  onCheckedChange={(checked) =>
                    setChosen((current) => {
                      const next = new Set(current);
                      if (checked) next.add(line.index);
                      else next.delete(line.index);
                      return next;
                    })
                  }
                />
                <span className={on ? "" : "text-muted-foreground line-through"}>{line.title}</span>
                <span className="text-xs text-muted-foreground">
                  {line.kind === "service" ? "اجرت" : "قطعه"}
                </span>
              </span>
              <span className="font-bold">{faToman(line.amount)}</span>
            </label>
          );
        })}
      </fieldset>
      <div className="flex flex-wrap items-center gap-2">
        <AlertDialog>
          <AlertDialogTrigger
            render={<Button size="sm" disabled={mutation.isPending || chosen.size === 0} />}
          >
            {partial ? "تأیید موارد انتخاب‌شده" : "تأیید و ادامه کار"}
          </AlertDialogTrigger>
          <AlertDialogContent lang="fa" dir="rtl">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {partial ? "موارد انتخاب‌شده تأیید شود؟" : "پیش‌فاکتور تأیید شود؟"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                با تأیید، {partial ? `${faPlainNumber(chosen.size)} مورد انتخاب‌شده از ` : ""}«
                {estimate.title}» به مبلغ {faToman(chosenTotal)} به فاکتور نهایی اضافه و کار انجام
                می‌شود.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>انصراف</AlertDialogCancel>
              <AlertDialogAction onClick={approve}>بله، تأیید می‌کنم</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog>
          <AlertDialogTrigger
            render={<Button size="sm" variant="outline" disabled={mutation.isPending} />}
          >
            هیچ‌کدام انجام نشود
          </AlertDialogTrigger>
          <AlertDialogContent lang="fa" dir="rtl">
            <AlertDialogHeader>
              <AlertDialogTitle>پیش‌فاکتور رد شود؟</AlertDialogTitle>
              <AlertDialogDescription>
                «{estimate.title}» انجام نمی‌شود و کار فقط طبق دستور اولیه ادامه می‌یابد. بعداً
                می‌توانید برای همین کار نوبت جدا بگیرید.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>انصراف</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  mutation.mutate({
                    repairOrderId: orderId,
                    estimateId: estimate.id,
                    approve: false,
                  })
                }
              >
                بله، رد شود
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
